import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Col, Form, Input, Row, Select, Upload, Avatar, App } from 'antd';
import { UploadOutlined, UserOutlined } from '@ant-design/icons';
import { getUserById, createUser, updateUser, uploadAvatar } from '../../services/users';
import { resolveMedia } from '../../utils/media';
import { ROLES_OPTIONS } from '../../utils/constants';

const { useApp } = App;

const alphaRegex = /^[A-Za-zÁÉÍÓÚÜÑáéíóúüñ\s]+$/;
const phoneRegex = /^\d{10}$/;

const UserUpsertPage = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  
  const navigate = useNavigate();
  const { message } = useApp();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(undefined);
  const [originalPhotoUrl, setOriginalPhotoUrl] = useState(null);

  useEffect(() => {
    if (!isEdit || !id) return;

    setLoading(true);
    getUserById(id)
      .then((user) => {
        form.setFieldsValue({
          firstName: user.data.firstName,
          lastName: user.data.lastName,
          secondLastName: user.data.secondLastName || undefined,
          email: user.data.email,
          phone: user.data.phone?.replace('+52', '') || undefined,
          role: user.data.role,
        });
        
        if (user.data.photoUrl) {
          setOriginalPhotoUrl(user.data.photoUrl);
          setAvatarPreview(resolveMedia(user.data.photoUrl));
        }
      })
      .catch(() => {
        message.error('No se pudo cargar el usuario');
      })
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit, form]);

  const uploadProps = useMemo(
    () => ({
      maxCount: 1, // Solo permitir un archivo
      beforeUpload: (file) => {
        const isImg = file.type.startsWith('image/');
        if (!isImg) {
          message.error('Solo se permiten imágenes (jpg, png, webp, etc.)');
          return Upload.LIST_IGNORE;
        }

        const isLt2M = file.size / 1024 / 1024 < 2;
        if (!isLt2M) {
          message.error('La imagen debe pesar menos de 2MB');
          return Upload.LIST_IGNORE;
        }

        setAvatarFile(file);
        setAvatarPreview(URL.createObjectURL(file));
        return false;
      },
      onRemove: () => {
        setAvatarFile(null);
        setAvatarPreview(undefined);
      },
      showUploadList: false,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  /**
   * Función que se ejecuta cuando se envía el formulario
   */
  const onSubmit = async (values) => {
    try {
      setLoading(true);

      let photoUrlToSend = undefined;
      
      if (avatarFile) {
        const uploadResult = await uploadAvatar(avatarFile);
        photoUrlToSend = uploadResult.url.avatarUrl || uploadResult;
      } else if (isEdit && originalPhotoUrl) {
        photoUrlToSend = originalPhotoUrl;
      }

      if (isEdit && id) {
        const updateData = {
          firstName: values.firstName.trim(),
          lastName: values.lastName.trim(),
          secondLastName: values.secondLastName?.trim() || undefined,
          phone: '+52'+values.phone || null,
          role: values.role,
          ...(photoUrlToSend !== undefined && { photoUrl: photoUrlToSend }),
        };

        await updateUser(id, updateData);
        message.success('Usuario actualizado correctamente');
      } else {
        const createData = {
          firstName: values.firstName.trim(),
          lastName: values.lastName.trim(),
          secondLastName: values.secondLastName?.trim() || undefined,
          email: values.email.trim(),
          phone: '+52'+values.phone || null,
          role: values.role,
          ...(photoUrlToSend !== undefined && { photoUrl: photoUrlToSend }),
        };

        await createUser(createData);
        message.success('Usuario agregado. Se envió el correo de bienvenida.');
      }

      navigate('/users/all');
    } catch (err) {
      // Extraer datos del error
      const errorData = err?.response?.data || err?.data || {};
      const errorObj = errorData?.error || {};
      const status = errorObj?.status || err?.response?.status || 500;
      const errorCode = errorObj?.code;
      const errorMessage = errorObj?.message || errorData?.message || err?.message || 'Error al guardar el usuario';
      
      // Si es un error de validación (422), mostrar errores en los campos del formulario
      if (status === 422 && (errorCode === 'VALIDATION_ERROR' || errorObj?.details?.issues)) {
        const issues = errorObj?.details?.issues || errorObj?.issues || [];
        
        console.log('Issues encontrados:', issues);
        
        if (issues.length > 0) {
          // Limpiar errores previos
          form.setFields([]);
          
          const fieldErrors = issues
            .filter((issue) => issue.path && issue.message) 
            .map((issue) => {
              let fieldPath = issue.path;
              
              if (typeof fieldPath === 'string') {
                fieldPath = [fieldPath];
              } else if (!Array.isArray(fieldPath)) {
                fieldPath = [String(fieldPath)];
              }
              
              console.log('Mapeando error para campo:', fieldPath, 'Mensaje:', issue.message);
              
              return {
                name: fieldPath,
                errors: [issue.message || 'Error de validación'],
              };
            });
          
          console.log('Field errors a establecer:', fieldErrors);
          
          if (fieldErrors.length > 0) {
            form.setFields(fieldErrors);
            console.log('Errores establecidos en el formulario');
          }
          
          message.error(errorMessage || 'Por favor, corrige los errores en el formulario');
        } else {
          message.error(errorMessage);
        }
      } else {
        message.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Card title={isEdit ? 'Editar usuario' : 'Agregar usuario'} loading={loading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={onSubmit}
          autoComplete="off"
        >
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item
                label="Nombre"
                name="firstName"
                rules={[
                  { required: true, message: 'El nombre es requerido' },
                  { pattern: alphaRegex, message: 'Solo se permiten letras y espacios' },
                ]}
              >
                <Input placeholder="Nombres" allowClear />
              </Form.Item>
            </Col>
            <Col xs={24} md={16}>
              <Form.Item
                label="Apellidos"
                name="lastName"
                rules={[
                  { required: true, message: 'El apellido es requerido' },
                  { pattern: alphaRegex, message: 'Solo se permiten letras y espacios' },
                ]}
              >
                <Input placeholder="Apellidos" allowClear />
              </Form.Item>
            </Col>
              {/* <Col xs={24} md={8}>
                <Form.Item
                  label="Apellido Materno"
                  name="secondLastName"
                  rules={[
                    { pattern: alphaRegex, message: 'Solo se permiten letras y espacios' },
                  ]}
                >
                  <Input placeholder="Apellido Materno (opcional)" allowClear />
                </Form.Item>
              </Col> */}
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Correo electrónico"
                name="email"
                rules={[
                  { required: !isEdit, message: 'El correo es requerido' },
                  { type: 'email', message: 'Correo electrónico inválido' },
                ]}
              >
                <Input
                  placeholder="contacto@ejemplo.com"
                  allowClear
                  disabled={isEdit}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Teléfono móvil"
                name="phone"
                rules={[
                  { pattern: phoneRegex, message: 'Debe tener exactamente 10 dígitos' },
                ]}
              >
                <Input
                  placeholder="0000000000"
                  maxLength={10}
                  onChange={(e) => {
                    const onlyDigits = e.target.value.replace(/\D/g, '');
                    form.setFieldsValue({ phone: onlyDigits });
                  }}
                  allowClear
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Rol"
                name="role"
                rules={[{ required: true, message: 'Debes seleccionar un rol' }]}
              >
                <Select
                  placeholder="Selecciona un rol"
                  options={ROLES_OPTIONS}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Foto de perfil">
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  {/* Avatar con preview de la imagen */}
                  <Avatar
                    size={64}
                    src={avatarPreview}
                    icon={<UserOutlined />}
                  />
                  {/* Botón para seleccionar imagen */}
                  <Upload {...uploadProps}>
                    <Button icon={<UploadOutlined />}>Elige una imagen</Button>
                  </Upload>
                </div>
              </Form.Item>
            </Col>
          </Row>

          <Row justify="end" gutter={12} style={{ marginTop: 24 }}>
            <Col>
              <Button onClick={() => navigate(-1)} disabled={loading}>
                Cancelar
              </Button>
            </Col>
            <Col>
              <Button type="primary" htmlType="submit" loading={loading}>
                {isEdit ? 'Guardar cambios' : 'Guardar'}
              </Button>
            </Col>
          </Row>
        </Form>
      </Card>
    </div>
  );
};

export default UserUpsertPage;

